import { useLayoutEffect, useState, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { FiBookmark, FiCheck } from 'react-icons/fi';
import { useAppStore } from '../../store/useAppStore';

interface Props {
  documentId: string;
  anchorRef: RefObject<HTMLElement | null>;
  onClose: () => void;
}

export function BookmarkPicker({ documentId, anchorRef, onClose }: Props) {
  const lists = useAppStore((state) => state.favoriteLists);
  const toggle = useAppStore((state) => state.toggleFavoriteListDocument);
  const [position, setPosition] = useState<{ left: number; top: number; width: number }>();

  useLayoutEffect(() => {
    const updatePosition = () => {
      const anchor = anchorRef.current;
      if (!anchor) return;
      const rect = anchor.getBoundingClientRect();
      const gutter = 8;
      const width = Math.min(300, window.innerWidth - gutter * 2);
      const left = Math.min(Math.max(gutter, rect.right - width), window.innerWidth - width - gutter);
      setPosition({ left, top: rect.bottom + gutter, width });
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('orientationchange', updatePosition);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('orientationchange', updatePosition);
    };
  }, [anchorRef]);

  const change = (listId: string, selected: boolean, name: string) => {
    if (selected && !window.confirm(`Remove this PDF from bookmark “${name}”?`)) return;
    toggle(listId, documentId);
  };

  if (!position) return null;

  return createPortal(
    <>
      <button className="bookmark-picker-scrim" aria-label="Close bookmark picker" onClick={onClose} />
      <div
        className="favorite-picker bookmark-picker-popover"
        style={{ ...position, '--bookmark-picker-top': `${position.top}px` } as React.CSSProperties}
        role="dialog"
        aria-label="Add PDF to bookmark collections"
      >
        <strong>Add to bookmarks</strong>
        <small>Select one or more bookmark collections</small>
        <div>
          {lists.length ? lists.map((list) => {
            const selected = list.documentIds.includes(documentId);
            return <button type="button" className={selected ? 'is-selected' : ''} key={list.id} onClick={() => change(list.id, selected, list.name)}><FiBookmark fill={selected ? 'currentColor' : 'none'} /><span>{list.name}</span>{selected && <FiCheck />}</button>;
          }) : <p>Create a bookmark collection from Navigation first.</p>}
        </div>
        <button type="button" className="favorite-picker-done" onClick={onClose}>Done</button>
      </div>
    </>,
    document.body,
  );
}
